import React from "react";
import { MapContainer, TileLayer, Marker, Popup, Polyline } from "react-leaflet";
import L from "leaflet";
import { Checkbox } from "../ui/checkbox";
import { Layers } from "lucide-react";
import { FASCE_LABEL, formatNumber } from "../../lib/constants";
import { makeNumberedIcon, depositoIcon } from "./leafletIcons";

// Fix default Leaflet icon (idempotente)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

export function GiroMap({ data, filteredMezzi, visibleMezzi, setVisibleMezzi, activeTab }) {
  const center = data?.deposito ? [data.deposito.lat, data.deposito.lng] : [43.78, 11.24];
  if (!data) return null;
  // B5: fallback difensivo — se non vengono passati filtri (es. usato dal giro operativo
  // da zone), usa tutti i mezzi di data come visibili.
  const mezziToRender = filteredMezzi ?? data.mezzi ?? [];
  const isVisible = (giroId, mezzoId) => {
    if (!visibleMezzi) return true;
    if (mezzoId && visibleMezzi[mezzoId] !== undefined) return visibleMezzi[mezzoId];
    if (visibleMezzi[giroId] !== undefined) return visibleMezzi[giroId];
    return true;
  };
  return (
    <div className="h-[300px] sm:h-[420px] relative">
      <MapContainer center={center} zoom={11} style={{ height: "100%", width: "100%" }} scrollWheelZoom>
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <Marker position={[data.deposito.lat, data.deposito.lng]} icon={depositoIcon()}>
          <Popup>{data.deposito.nome}</Popup>
        </Marker>
        {mezziToRender.map((m) => {
          if (!isVisible(m.giroId, m.mezzo?.id)) return null;
          const color = m.mezzo?.colore || "#9E9E9E";
          const points = [
            [data.deposito.lat, data.deposito.lng],
            ...m.fermate.filter((s) => s.lat && s.lng).map((s) => [s.lat, s.lng]),
            [data.deposito.lat, data.deposito.lng],
          ];
          return (
            <React.Fragment key={m.giroId}>
              <Polyline positions={points} pathOptions={{ color, weight: 4, opacity: 0.7 }} />
              {m.fermate.map((s, i) => s.lat && s.lng && (
                <Marker key={s.id} position={[s.lat, s.lng]} icon={makeNumberedIcon(color, i + 1)}>
                  <Popup>
                    <div className="font-semibold">{s.ragioneSociale}</div>
                    <div className="text-xs text-slate-600">{s.indirizzo}</div>
                    <div className="text-xs text-slate-500">{FASCE_LABEL[s.fasciaOraria] || "-"} · {s.orarioRitiro}</div>
                  </Popup>
                </Marker>
              ))}
            </React.Fragment>
          );
        })}
      </MapContainer>
      {activeTab === "ALL" && (
        <div className="absolute top-2 right-2 bg-white rounded-lg border border-slate-200 shadow-md p-2 space-y-1.5 z-[1000] max-w-[260px]" data-testid="map-legend">
          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 flex items-center gap-1 px-1">
            <Layers size={12} /> Mezzi attivi oggi
          </div>
          {data.mezzi.filter((m) => m.mezzo).map((m) => (
            <label key={m.mezzo.id} className="flex items-start gap-2 text-xs cursor-pointer px-1.5 py-1.5 hover:bg-slate-50 rounded border border-transparent hover:border-slate-100">
              <Checkbox
                checked={!!visibleMezzi[m.mezzo.id]}
                onCheckedChange={(v) => setVisibleMezzi({ ...visibleMezzi, [m.mezzo.id]: !!v })}
                data-testid={`toggle-${m.mezzo.id}`}
                className="mt-0.5"
              />
              <span className="h-3 w-3 rounded-full shrink-0 mt-1" style={{ backgroundColor: m.mezzo.colore }} />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-slate-800 truncate text-[11px]">{m.mezzo.nome}</div>
                <div className="text-[10px] text-slate-500 truncate">{m.zona}</div>
                <div className="text-[10px] text-slate-600 mt-0.5">
                  <span className="font-medium">{m.fermate.length}</span> tappe ·
                  <span className="font-medium ml-1">{formatNumber(m.totalKm)}</span> km
                </div>
              </div>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
